import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, CheckCircle2, FileDown, Loader2, Upload } from 'lucide-react';
import { bauvorhabenApi, contactsApi, gewerkeApi, invoicesApi } from '@/services/api';
import { emitAppEvent } from '@/utils/appEvents';
import { deriveNetTax } from './cellEdit';
import { InvoiceContactCell } from './gridCells';
import {
  parseInvoiceTable, parseInvoiceRows, detectDelimiter, splitDelimitedLine,
  type InvoiceImportField, type ParsedInvoiceRow, type ParseResult,
} from '@/utils/invoiceImport';
import type { InvoiceBulkResult, InvoiceWritePayload } from '@/types';

/**
 * Excel-/CSV-Übernahme für Rechnungen (Toolbar "Importieren", admin,
 * max 200 Zeilen pro Durchlauf): 1. Quelle (Paste = Excel-Copy/TSV oder
 * .csv/.txt), 2. Vorschau & Zuordnung (Header-Mapping, Kontakt-Match je
 * eindeutigem Namen, BV/Gewerk-Match gegen geladene Listen, Zeilen-
 * Checkboxen), 3. Ergebnis (partial success des Bulk-Endpoints).
 */

const FIELD_LABELS: Record<InvoiceImportField, string> = {
  invoice_number: 'Rechnungsnummer',
  invoice_date: 'Rechnungsdatum',
  due_date: 'Fällig',
  contact: 'Lieferant/Kunde',
  bauvorhaben: 'Bauvorhaben',
  gewerk: 'Gewerk',
  description: 'Betreff',
  amount_net: 'Netto',
  amount_tax: 'MwSt',
  tax_rate_percent: 'MwSt-Satz',
  amount_gross: 'Brutto',
  direction: 'Richtung',
};

const MAPPABLE_FIELDS: InvoiceImportField[] = [
  'invoice_number', 'invoice_date', 'due_date', 'contact', 'bauvorhaben',
  'gewerk', 'description', 'amount_net', 'tax_rate_percent', 'amount_tax',
  'amount_gross', 'direction',
];

const normalizeName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const contactLabel = (c: { first_name?: string | null; last_name?: string | null; company_name?: string | null; display_name?: string | null }): string =>
  c.display_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || '';

const formatMoney = (value: number | null): string =>
  value == null
    ? '—'
    : value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const MAX_ROWS = 200;

export function InvoiceImportModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rawText, setRawText] = useState('');
  const [delimiterOverride, setDelimiterOverride] = useState<'' | '\t' | ';' | ','>('');
  const [parse, setParse] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState('');

  // Zuordnungsdaten (BV/Gewerk-Kataloge + Kontakt-Match je eindeutigem Namen)
  const [bauvorhabenOptions, setBauvorhabenOptions] = useState<Array<{ id: number; name: string; project_number?: string | null }>>([]);
  const [gewerkOptions, setGewerkOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [isMatching, setIsMatching] = useState(false);
  const [result, setResult] = useState<InvoiceBulkResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');

  // ---------- Schritt 2: Daten laden + Matching ----------
  // Ref-Zugriff statt parse-Dep: der Match läuft EINMAL beim Eintritt in
  // Schritt 2 — sonst würde jede Zeilen-Änderung das Matching neu starten
  // und manuelle Zuordnungen zurücksetzen.
  const parseRef = useRef<ParseResult | null>(parse);
  parseRef.current = parse;

  useEffect(() => {
    if (step !== 2 || !parseRef.current) return;
    const parsed = parseRef.current;
    let cancelled = false;
    setIsMatching(true);

    (async () => {
      // BV seitenweise (per_page-Cap 100) + Gewerke-Katalog.
      const bvList: Array<{ id: number; name: string; project_number?: string | null }> = [];
      for (let p = 1; p <= 20; p++) {
        const response = await bauvorhabenApi.getAll({ page: p, per_page: 100, sort: 'name_asc' });
        bvList.push(...response.data.data.map((b) => ({ id: b.id, name: b.name, project_number: b.project_number })));
        const totalPages = response.data.pagination?.total_pages ?? p;
        if (p >= totalPages) break;
      }
      const gewerkResponse = await gewerkeApi.getAll();
      const gewerke = gewerkResponse.data.data.phases.flatMap((phase) => phase.gewerke.map((g) => ({ id: g.id, name: g.name })));
      if (cancelled) return;
      setBauvorhabenOptions(bvList);
      setGewerkOptions(gewerke);

      // BV/Gewerk clientseitig zuordnen (Name/Nummer, normalisiert).
      const bvByNorm = new Map<string, number>();
      for (const b of bvList) {
        if (b.name) bvByNorm.set(normalizeName(b.name), b.id);
        if (b.project_number) bvByNorm.set(normalizeName(b.project_number), b.id);
      }
      const gewerkByNorm = new Map<string, number>();
      for (const g of gewerke) gewerkByNorm.set(normalizeName(g.name), g.id);

      setParse((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((row) => ({
            ...row,
            bauvorhaben_id: row.bauvorhaben_ref ? bvByNorm.get(normalizeName(row.bauvorhaben_ref)) ?? null : null,
            gewerk_id: row.gewerk_ref ? gewerkByNorm.get(normalizeName(row.gewerk_ref)) ?? null : null,
          })),
        };
      });

      // Kontakt-Match je eindeutigem Lieferantennamen (gecachter Such-Call).
      const uniqueNames = Array.from(
        new Set(parsed.rows.map((r) => r.contact_name?.trim()).filter((n): n is string => !!n))
      );
      const matches = new Map<string, number | null>();
      await Promise.all(uniqueNames.map(async (name) => {
        try {
          const response = await contactsApi.getAll(1, name);
          const normalizedQuery = normalizeName(name);
          const hit = response.data.data.find(
            (c) => normalizeName(contactLabel(c)) === normalizedQuery
          ) ?? response.data.data[0] ?? null;
          matches.set(name, hit ? hit.id : null);
        } catch {
          matches.set(name, null);
        }
      }));
      if (cancelled) return;
      setParse((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map((row) => ({
            ...row,
            contact_id: row.contact_name ? matches.get(row.contact_name.trim()) ?? null : null,
          })),
        };
      });
      setIsMatching(false);
    })().catch(() => {
      if (!cancelled) {
        setIsMatching(false);
        setParseError('Fehler beim Laden der Zuordnungslisten');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [step]);

  // ---------- Zeilen aktualisieren ----------
  const updateRow = (rowNumber: number, patch: Partial<ParsedInvoiceRow>) => {
    setParse((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((row) => (row.rowNumber === rowNumber ? { ...row, ...patch } : row)),
      };
    });
  };

  const setMapping = (field: InvoiceImportField, columnIndex: number) => {
    setParse((prev) => {
      if (!prev) return prev;
      // Feld auf die neue Spalte setzen; die alte Zuordnung desselben
      // Index aus den anderen Feldern entfernen (Spalte nur 1× vergeben).
      const mapping = { ...prev.mapping };
      for (const f of MAPPABLE_FIELDS) {
        if (f !== field && mapping[f] === columnIndex) mapping[f] = -1;
      }
      mapping[field] = columnIndex;
      const rows = parseInvoiceRows(
        prev.headers,
        mapping,
        dataRowsOf(rawText, delimiterOverride)
      );
      // Manuelle Zuordnungen/Selektionen der bisherigen Zeilen erhalten
      // (neues Mapping ändert nur die Rohwerte-Zuordnung).
      const previousByRow = new Map<number, ParsedInvoiceRow>(prev.rows.map((r) => [r.rowNumber, r]));
      const merged = rows.map((row) => {
        const previous = previousByRow.get(row.rowNumber);
        return previous
          ? {
              ...row,
              selected: previous.selected,
              contact_id: previous.contact_id,
              bauvorhaben_id: previous.bauvorhaben_id,
              gewerk_id: previous.gewerk_id,
            }
          : row;
      });
      return { ...prev, mapping, rows: merged };
    });
  };

  // ---------- Statistik ----------
  const importableRows = useMemo(
    () => (parse?.rows ?? []).filter((row) => row.selected && row.errors.length === 0),
    [parse]
  );
  const stats = useMemo(() => {
    const rows = parse?.rows ?? [];
    const duplicates = rows.filter((r) => r.errors.some((e) => e.startsWith('Doppelt'))).length;
    return {
      total: rows.length,
      importable: importableRows.length,
      blocked: rows.filter((r) => !r.selected || r.errors.length > 0).length,
      duplicates,
      unmatchedContacts: rows.filter((r) => r.selected && r.errors.length === 0 && r.contact_name && !r.contact_id).length,
    };
  }, [parse, importableRows]);

  // ---------- Aktionen ----------
  const handleParse = () => {
    setParseError('');
    if (rawText.trim() === '') {
      setParseError('Bitte Daten einfügen oder eine Datei wählen.');
      return;
    }
    try {
      const parsed = parseInvoiceTable(rawText, delimiterOverride || undefined);
      if (parsed.rows.length === 0) {
        setParseError('Keine Datenzeilen erkannt — bitte Prüfen, ob die erste Zeile die Überschriften enthält.');
        return;
      }
      setParse(parsed);
      setStep(2);
    } catch (err) {
      setParseError('Die Daten konnten nicht geparst werden. Bitte Trennzeichen prüfen.');
    }
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setRawText(String(reader.result ?? ''));
      setParse(null);
    };
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = () => {
    if (!parse) return;
    const rows = importableRows.slice(0, MAX_ROWS);
    if (rows.length === 0) return;
    setIsImporting(true);
    setImportError('');
    const payloads: InvoiceWritePayload[] = rows.map((row) => {
      // Fehlendes Netto/MwSt: beim angegebenen Satz aus dem Brutto ableiten.
      let amount_net = row.amount_net;
      let amount_tax = row.amount_tax;
      if ((amount_net == null || amount_tax == null) && row.tax_rate_percent != null && row.amount_gross != null) {
        const derived = deriveNetTax(row.amount_gross, row.tax_rate_percent);
        if (derived) {
          amount_net = derived.net;
          amount_tax = derived.tax;
        }
      }
      return {
        direction: row.direction,
        invoice_number: row.invoice_number,
        contact_id: row.contact_id,
        bauvorhaben_id: row.bauvorhaben_id ?? null,
        gewerk_id: row.gewerk_id ?? null,
        amount_net,
        amount_tax,
        amount_gross: row.amount_gross ?? 0,
        tax_rate_percent: row.tax_rate_percent,
        invoice_date: row.invoice_date ?? '',
        due_date: row.due_date,
        description: row.description,
        notes: null,
      };
    });
    invoicesApi
      .bulkCreate(payloads)
      .then((response) => {
        setResult(response.data.data);
        setStep(3);
        emitAppEvent('rechnungen:changed');
      })
      .catch((err) => {
        setImportError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Import');
      })
      .finally(() => setIsImporting(false));
  };

  // ---------- Render ----------
  const rows = parse?.rows ?? [];

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900/40 p-4" onClick={onClose}>
      <div
        className="flex h-[85vh] w-full max-w-6xl flex-col rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kopf */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Rechnungen importieren</h2>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" title="Schließen">
            ✕
          </button>
        </div>

        {/* Schritt-Anzeige */}
        <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-2 text-sm">
          {(['Quelle', 'Vorschau & Zuordnung', 'Ergebnis'] as const).map((label, i) => (
            <span
              key={label}
              className={`rounded-full px-3 py-0.5 text-xs font-medium ${
                step === i + 1 ? 'bg-primary-600 text-white' : step > i + 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {i + 1}. {label}
            </span>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* Schritt 1: Quelle */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Excel-Zeilen kopieren und einfügen (Zellbereiche = Tabs, bevorzugt) oder eine
                <strong>.csv/.txt</strong>-Datei wählen. <strong>.xlsx</strong> wird nicht gelesen — bitte in Excel
                über „Kopieren" oder „Als CSV speichern" bereitstellen. Maximal {MAX_ROWS} Zeilen pro Durchlauf.
              </p>
              <div className="flex items-center gap-2">
                <label className="btn btn-secondary inline-flex cursor-pointer items-center gap-2 text-sm">
                  <FileDown className="h-4 w-4" />
                  Datei wählen (.csv/.txt)
                  <input
                    type="file"
                    accept=".csv,.txt,text/csv,text/plain"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFile(file);
                      e.target.value = '';
                    }}
                  />
                </label>
                <select
                  value={delimiterOverride}
                  onChange={(e) => setDelimiterOverride(e.target.value as '' | '\t' | ';' | ',')}
                  className="input w-auto py-1.5 text-sm"
                  title="Trennzeichen (leer = automatisch erkennen)"
                >
                  <option value="">Trennzeichen: automatisch</option>
                  <option value="\t">Tabs (Excel-Kopie)</option>
                  <option value=";">Semikolon (;)</option>
                  <option value=",">Komma (,)</option>
                </select>
              </div>
              <textarea
                value={rawText}
                onChange={(e) => {
                  setRawText(e.target.value);
                  setParse(null);
                }}
                rows={10}
                placeholder={'Rechnungsnummer\tRechnungsdatum\tLieferant\tBauvorhaben\tNetto\tMwSt-Satz\tMwSt\tBrutto\tBetreff'}
                className="input font-mono text-xs"
              />
              {parseError && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {parseError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="btn btn-secondary">Abbrechen</button>
                <button onClick={handleParse} className="btn btn-primary">Weiter zur Zuordnung</button>
              </div>
            </div>
          )}

          {/* Schritt 2: Vorschau & Zuordnung */}
          {step === 2 && parse && (
            <div className="space-y-3">
              {/* Header-Mapping */}
              <div>
                <p className="mb-2 text-sm text-gray-600">
                  Spalten-Zuordnung — automatisch erkannt, bei Bedarf korrigieren:
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-600">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Feld</th>
                        <th className="px-2 py-1.5 text-left">Spalte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MAPPABLE_FIELDS.map((field) => (
                        <tr key={field} className="border-t border-gray-100">
                          <td className="px-2 py-1 text-gray-900">
                            {FIELD_LABELS[field]}
                            {field === 'invoice_number' || field === 'invoice_date' || field === 'amount_gross' ? ' *' : ''}
                          </td>
                          <td className="px-2 py-1">
                            <select
                              value={parse.mapping[field]}
                              onChange={(e) => setMapping(field, Number(e.target.value))}
                              className="input w-auto max-w-[260px] py-1 text-xs"
                            >
                              <option value={-1}>— nicht zugeordnet —</option>
                              {parse.headers.map((header, index) => (
                                <option key={index} value={index}>
                                  {index + 1}. {header}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Zeilen-Vorschau */}
              {isMatching ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Kontakte & Bauvorhaben werden zugeordnet…
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full border-separate border-spacing-0 text-xs">
                    <thead className="sticky top-0 bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-1.5 py-1.5">✓</th>
                        <th className="px-1.5 py-1.5 text-left">Zeile</th>
                        <th className="px-1.5 py-1.5 text-left">Rechnungsnummer</th>
                        <th className="px-1.5 py-1.5 text-left">Datum</th>
                        <th className="px-1.5 py-1.5 text-left">Lieferant/Kunde</th>
                        <th className="px-1.5 py-1.5 text-left">Bauvorhaben</th>
                        <th className="px-1.5 py-1.5 text-left">Gewerk</th>
                        <th className="px-1.5 py-1.5 text-left">Betreff</th>
                        <th className="px-1.5 py-1.5 text-right">Netto</th>
                        <th className="px-1.5 py-1.5 text-right">MwSt-Satz</th>
                        <th className="px-1.5 py-1.5 text-right">MwSt</th>
                        <th className="px-1.5 py-1.5 text-right">Brutto</th>
                        <th className="px-1.5 py-1.5">E/R</th>
                        <th className="px-1.5 py-1.5 text-left">Hinweise</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const blocked = row.errors.length > 0;
                        return (
                          <tr key={row.rowNumber} className={`border-t border-gray-100 ${blocked ? 'bg-red-50/60' : ''}`}>
                            <td className="px-1.5 py-1 text-center">
                              <input
                                type="checkbox"
                                checked={row.selected}
                                onChange={(e) => updateRow(row.rowNumber, { selected: e.target.checked })}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600"
                              />
                            </td>
                            <td className="px-1.5 py-1 text-gray-400">{row.rowNumber}</td>
                            <td className="px-1.5 py-1 font-medium text-gray-900">{row.invoice_number || '—'}</td>
                            <td className="px-1.5 py-1 text-gray-900">{row.invoice_date ?? <span className="text-red-500">unlesbar</span>}</td>
                            <td className="px-1.5 py-1">
                              <InvoiceContactCell
                                contactId={row.contact_id}
                                contactName={row.contact_name}
                                editable={!blocked}
                                onSelect={(id) => updateRow(row.rowNumber, { contact_id: id })}
                              />
                            </td>
                            <td className="px-1.5 py-1">
                              <select
                                value={row.bauvorhaben_id ?? ''}
                                onChange={(e) => updateRow(row.rowNumber, { bauvorhaben_id: e.target.value === '' ? null : Number(e.target.value) })}
                                className="max-w-[160px] rounded border border-transparent bg-transparent py-0.5 text-xs hover:border-gray-300"
                                title={row.bauvorhaben_ref || 'Bauvorhaben wählen'}
                              >
                                <option value="">— Keine —</option>
                                {bauvorhabenOptions.map((o) => (
                                  <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-1.5 py-1">
                              <select
                                value={row.gewerk_id ?? ''}
                                onChange={(e) => updateRow(row.rowNumber, { gewerk_id: e.target.value === '' ? null : Number(e.target.value) })}
                                className="max-w-[140px] rounded border border-transparent bg-transparent py-0.5 text-xs hover:border-gray-300"
                                title={row.gewerk_ref || 'Gewerk wählen'}
                              >
                                <option value="">— Keine —</option>
                                {gewerkOptions.map((o) => (
                                  <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="max-w-[180px] truncate px-1.5 py-1 text-gray-900" title={row.description ?? ''}>
                              {row.description || '—'}
                            </td>
                            <td className="px-1.5 py-1 text-right tabular-nums text-gray-900">{formatMoney(row.amount_net)}</td>
                            <td className="px-1.5 py-1 text-right tabular-nums text-gray-900">
                              {row.tax_rate_percent != null ? `${row.tax_rate_percent} %` : '—'}
                            </td>
                            <td className="px-1.5 py-1 text-right tabular-nums text-gray-900">{formatMoney(row.amount_tax)}</td>
                            <td className="px-1.5 py-1 text-right font-semibold tabular-nums text-gray-900">{formatMoney(row.amount_gross)}</td>
                            <td className="px-1.5 py-1 text-center">
                              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${row.direction === 'eingang' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-800'}`}>
                                {row.direction === 'eingang' ? 'E' : 'R'}
                              </span>
                            </td>
                            <td className="max-w-[220px] px-1.5 py-1">
                              {row.errors.map((e) => (
                                <p key={e} className="text-red-600">{e}</p>
                              ))}
                              {row.warnings.map((w) => (
                                <p key={w} className="text-amber-600">{w}</p>
                              ))}
                              {row.errors.length === 0 && row.warnings.length === 0 && row.contact_name && !row.contact_id && (
                                <p className="text-amber-600">Lieferant nicht zugeordnet — importiert ohne Kontakt</p>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {rows.length === 0 && (
                        <tr><td colSpan={14} className="py-6 text-center text-gray-500">Keine Zeilen</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className="text-gray-600">
                  <strong>{stats.importable}</strong> von <strong>{stats.total}</strong> importierbar
                  {stats.duplicates > 0 && <> · {stats.duplicates} Duplikate in der Liste</>}
                  {stats.blocked > 0 && <> · {stats.blocked} blockiert/abgewählt</>}
                  {stats.unmatchedContacts > 0 && <> · {stats.unmatchedContacts} ohne Lieferant-Zuordnung</>}
                  {stats.importable > MAX_ROWS && <> · <span className="text-amber-700">es werden nur die ersten {MAX_ROWS} Zeilen importiert</span></>}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setStep(1)} className="btn btn-secondary inline-flex items-center gap-1.5">
                    <ArrowLeft className="h-4 w-4" />
                    Zurück
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={isImporting || stats.importable === 0}
                    className="btn btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {isImporting ? 'Importiert…' : `${Math.min(stats.importable, MAX_ROWS)} Rechnungen importieren`}
                  </button>
                </div>
              </div>
              {parseError && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {parseError}
                </div>
              )}
              {importError && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {importError}
                </div>
              )}
            </div>
          )}

          {/* Schritt 3: Ergebnis */}
          {step === 3 && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                {result.created} Rechnungen erstellt
                {result.skipped > 0 && `, ${result.skipped} übersprungen`}
                .
              </div>
              {result.errors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <p className="mb-2 font-medium">Übersprungene Zeilen:</p>
                  <ul className="space-y-1">
                    {result.errors.map((e, i) => (
                      <li key={i}>
                        Zeile {e.row}{e.invoice_number ? ` (${e.invoice_number})` : ''}: {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={onClose} className="btn btn-primary">Fertig</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Hilfsfunktionen ----------

/** Rohdaten erneut parsen mit einem geänderten Mapping (setMapping). */
function dataRowsOf(rawText: string, delimiterOverride: string): string[][] {
  const delimiter = delimiterOverride || detectDelimiter(rawText);
  const lines = rawText.split(/\r?\n/).filter((line) => line.trim() !== '');
  return lines.slice(1).map((line) => splitDelimitedLine(line, delimiter));
}